import { capitalize } from 'lodash';
// @ts-expect-error - no types available for react-select
import Select from 'react-select';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { Offer } from '@upstart/harmony/lib/legos/paymentTypes';
import { isCashOfferData } from '@upstart/harmony/lib/validation/offer';
import { OfferData } from '@upstart/harmony/lib/legos/deskingTypes';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import { Car, IncentivesProgram } from '@upstart/harmony/lib/legos/carTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import Modal from 'react-modal';
import React, { useEffect, useState } from 'react';
import { Action, ModalType } from 'util/amplitudeEvents';
import Tooltip from 'components/Tooltip';
import { Icon } from 'semantic-ui-react';
import { RATE_PRECISION } from 'components/DeskingEditor/constants';
import { modalOverlayBackgroundColor } from '../../../util/constants';
import Input, { formatToPrecision } from '../Input';
import './EditMarketscanIncentives.scss';
import { IncentiveService } from '../../../services/IncentivesService';
import ProgramDetailsModal from './ProgramDetailsModal';
import IncentivesTable, { Item } from './IncentivesTable';
import { IncentiveData, Lead, Rebate } from './rebate-types';
import {
  leadToIncentiveProgram,
  rebateToIncentiveProgram,
} from './offer-utils';

type EditMarketscanIncentivesProps = {
  financeType: FinanceType;
  offers: Offer[];
  activeOffer: OfferData;
  deskingOfferId: string;
  deskingId: string;
  handleClose(): void;
  onSave(condensedOfferPairs: CondensedOfferPair[]): void;
  deal: Deal;
  dealer: Dealer;
  car: Car;
  isOnePay: boolean;
  sendAmplitudeEvent?: (events: ModalType, action: Action) => void;
  disabled?: boolean;
};

type CondensedOfferPair = {
  offer: Offer;
  consolidated: {
    cash: number;
    dealerCash: number;
    apr?: number;
    programs: IncentivesProgram[];
    selectedCustomerTypeId?: number;
  };
};

type CashType = 'dealerCash' | 'otherCash';

type CompatibilitiesResponse = {
  // data is a map of leadId to compatible rebateIds
  // if a leadId is not present, it is compatible
  data: {
    [leadId: number]: number[];
  };
};

const scrollableModalStyles = {
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: modalOverlayBackgroundColor,
  },
  content: {
    top: 'unset',
    bottom: 'unset',
    left: 'unset',
    right: 'unset',
    minWidth: '70vw',
    maxWidth: '100vw',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
} as const;

const createConsolidatedOfferPair = (
  selectedLead: Lead,
  selectedRebates: Rebate[],
  incompatibleRebateIds: Set<number>,
  offer: Offer,
  selectedCustomerTypeId?: number,
): CondensedOfferPair => {
  const leadProgram = leadToIncentiveProgram(selectedLead);
  const programs: IncentivesProgram[] = [
    leadProgram,
    ...selectedRebates.map(rebate => {
      const program = rebateToIncentiveProgram(rebate);
      if (!incompatibleRebateIds.has(rebate.rebateId)) {
        program.appliedCash =
          rebate.values.find(v => v.leadId === selectedLead.leadId)
            ?.otherCash ?? 0;
        program.appliedDealerCash =
          rebate.values.find(v => v.leadId === selectedLead.leadId)
            ?.dealerCash ?? 0;
      }
      return program;
    }),
  ];

  const consolidated: {
    cash: number;
    dealerCash: number;
    apr?: number;
    programs: IncentivesProgram[];
    selectedCustomerTypeId?: number;
  } = {
    cash: programs.reduce((acc, p) => acc + p.appliedCash, 0),
    dealerCash: programs.reduce(
      (acc, p) => acc + (p.appliedDealerCash ?? 0),
      0,
    ),
    apr: leadProgram.appliedApr ?? undefined,
    programs,
    selectedCustomerTypeId,
  };

  return {
    consolidated,
    offer,
  };
};

const getLeadFromOffer = (data: IncentiveData, offer: Offer): Lead => {
  const [foundLead] = data.leads.filter(lead =>
    offer.programs?.find(
      p =>
        p.programNumber === lead.programId.toString() &&
        p.id === lead.leadId.toString(),
    ),
  );

  return foundLead;
};

/**
 * Matches incentive rebate Ids to the offer's programs.  We store the applied incentives ids
 * for an offer on the offer.programs[].programNumber property therefore match on that
 */
const getRebateIdsFromOffer = (data: IncentiveData, offer: Offer): number[] => {
  const selectedRebates = data.rebates.filter(rebate =>
    offer.programs?.find(p => p.programNumber === rebate.rebateId.toString()),
  );

  return selectedRebates.map(val => val.rebateId);
};

function EditMarketscanIncentivesForm({
  activeOffer,
  deskingOfferId,
  deskingId,
  handleClose,
  sendAmplitudeEvent,
  onSave,
  disabled = false,
  isOnePay,
}: EditMarketscanIncentivesProps) {
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    null,
  );
  const [selectedRebates, setSelectedRebates] = useState<Set<number>>(
    new Set(),
  );
  const [incompatibleIncentives, setIncompatibleIncentives] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [selectedCustomerType, setSelectedCustomerType] = useState<
    number | null
  >(null);

  const [isLoadingIncentives, setIsLoadingIncentives] = useState(false);
  const [
    isLoadingIncentivesCompatibility,
    setIsLoadingIncentivesCompatibility,
  ] = useState(false);
  const [
    displayNoIncentivesAvailable,
    setDisplayNoIncentivesAvailable,
  ] = useState(false);

  const [incentivesData, setIncentivesData] = useState<IncentiveData>({
    leads: [],
    rebates: [],
    customerTypes: [],
  });

  const loadIncentiveOffers = async () => {
    setIsLoadingIncentives(true);
    const incentivesResponse = await IncentiveService.getIncentives(
      deskingId,
      deskingOfferId,
      selectedCustomerType ?? undefined,
    );

    // Sort alphabetically ASC
    incentivesResponse.data.rebates.sort((a: Rebate, b: Rebate) => {
      if (a.name > b.name) {
        return -1;
      }
      if (a.name < b.name) {
        return 1;
      }
      return 0;
    });

    // Move Info only rebates to bottom of the table
    incentivesResponse.data.rebates.sort((a: Rebate) =>
      a.isInformationOnly ? 1 : -1,
    );

    // Move Leading rebates to top of the table
    incentivesResponse.data.rebates.sort((a: Rebate) =>
      a.isLeadingRebate ? -1 : 1,
    );

    setIncentivesData(incentivesResponse.data);
    setIsLoadingIncentives(false);
  };

  const getCompatibleIncentives = async () => {
    if (!selectedLeadId || isLoadingIncentivesCompatibility) {
      return;
    }

    // if only one rebate is selected, reset the incompatible incentives
    // rest of the logic is handled by total calculations
    if (selectedRebates.size <= 1) {
      // TODO: verify this is still works
      setIncompatibleIncentives(new Map());
      return;
    }

    setIsLoadingIncentivesCompatibility(true);
    const response = await IncentiveService.getCompatibleIncentives<
      CompatibilitiesResponse
    >(deskingId, [selectedLeadId, ...Array.from(selectedRebates)]);

    const incompatibleMap = new Map<number, Set<number>>();
    for (const [leadId, rebateIds] of Object.entries(response.data)) {
      // create a new set of the current selected rebates and remove the compatible ones
      // this leaves us with the incompatible incentives
      const diff = new Set(selectedRebates);
      for (const elem of rebateIds) {
        diff.delete(elem);
      }

      // if the current lead is in the diff set, remove it as it is a leading rebate
      if (diff.has(parseInt(leadId, 10))) {
        diff.delete(parseInt(leadId, 10));
      }

      incompatibleMap.set(parseInt(leadId, 10), diff);
    }

    setIncompatibleIncentives(incompatibleMap);

    setIsLoadingIncentivesCompatibility(false);
  };

  if (
    selectedCustomerType === null &&
    activeOffer && // this can apparently be `false` sometimes
    activeOffer.selectedCustomerTypeId !== undefined &&
    activeOffer.selectedCustomerTypeId !== null
  ) {
    setSelectedCustomerType(activeOffer.selectedCustomerTypeId);
  }

  useEffect(() => {
    // if no incentives are available, set that state and early return
    if (incentivesData.rebates.length === 0) {
      setDisplayNoIncentivesAvailable(true);
      return;
    }

    // we have incentives, let's ensure the proper incentives are selected from the current desking offer
    setDisplayNoIncentivesAvailable(false);

    const selectedLead = getLeadFromOffer(incentivesData, activeOffer);

    if (selectedLead) {
      setSelectedLeadId(selectedLead.leadId);
      setSelectedProgramId(selectedLead.programId);
    } else {
      setSelectedLeadId(incentivesData.leads[0].leadId);
      setSelectedProgramId(incentivesData.leads[0].programId);
    }

    const offerPreSelectedRebates = getRebateIdsFromOffer(
      incentivesData,
      activeOffer,
    );

    setSelectedRebates(new Set(offerPreSelectedRebates));

    if (selectedCustomerType === null) {
      setSelectedCustomerType(
        incentivesData.customerTypes.find(c => c.isSelected)?.id ?? null,
      );
    }
  }, [incentivesData]);

  // API requests
  useEffect(() => {
    loadIncentiveOffers().catch(_err => {});
  }, [selectedCustomerType]);

  useEffect(() => {
    getCompatibleIncentives().catch(_err => {});
  }, [selectedRebates, selectedLeadId]);

  const toggleSelectOffer = async (rebate: Rebate) => {
    setSelectedRebates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rebate.rebateId)) {
        newSet.delete(rebate.rebateId);
      } else {
        newSet.add(rebate.rebateId);
      }
      return newSet;
    });
  };

  const onSaveClicked = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    const lead = incentivesData.leads.find(
      l => l.leadId === selectedLeadId && l.programId === selectedProgramId,
    );
    if (lead) {
      const rebates = incentivesData.rebates.filter(r =>
        selectedRebates.has(r.rebateId),
      );
      onSave([
        createConsolidatedOfferPair(
          lead,
          rebates,
          incompatibleIncentives.get(lead.leadId) || new Set(),
          activeOffer,
          selectedCustomerType ?? undefined,
        ),
      ]);
      if (sendAmplitudeEvent) {
        sendAmplitudeEvent(ModalType.INCENTIVES, Action.FINISH);
      }
    }
    handleClose();
  };

  const renderIncentivesSelector = (rebate: Rebate) => {
    const checked = selectedRebates.has(rebate.rebateId);
    const isExpired = Date.now() > Date.parse(rebate.expiresAt);

    if (isExpired) {
      return (
        <div className="incentive-editor-warning-message">{'Expired'}</div>
      );
    }

    if (rebate.isInformationOnly) {
      return null;
    }

    return (
      <Input
        onChange={() => toggleSelectOffer(rebate)}
        checked={checked}
        type="checkbox"
        disabled={rebate.isLeadingRebate}
      />
    );
  };

  const selectedClass = (lead: Lead) =>
    selectedLeadId === lead.leadId && selectedProgramId === lead.programId
      ? 'selected'
      : '';

  const appliedClass = (lead: Lead, rebate: Rebate) => {
    // if the given lead id doesn't match the selected lead id, or the rebate isn't selected, return empty string
    if (
      selectedLeadId !== lead.leadId ||
      selectedProgramId !== lead.programId ||
      !selectedRebates.has(rebate.rebateId) ||
      incompatibleIncentives.get(lead.leadId)?.has(rebate.rebateId)
    ) {
      return '';
    }

    return 'applied';
  };

  const incompatibleClass = (lead: Lead, rebate: Rebate) =>
    incompatibleIncentives.get(lead.leadId)?.has(rebate.rebateId)
      ? 'incompatible'
      : '';

  const boldClass = (rebate: Rebate) =>
    selectedRebates.has(rebate.rebateId) ? 'bold' : '';

  const getHeaders = () => {
    if (isLoadingIncentives || displayNoIncentivesAvailable) {
      return [];
    }

    const headers: Item[] = [
      { content: '' },
      { content: 'Select all eligible rebates' },
    ];

    const getAprOrMoneyFactor = (lead: Lead) => {
      if (lead.apr) {
        return `${lead.apr}% APR`;
      }

      const moneyFactor = isOnePay ? lead.onePayMoneyFactor : lead.moneyFactor;

      if (moneyFactor) {
        const factorType = isOnePay ? 'One Pay Money Factor' : 'Money Factor';
        return `${formatToPrecision(
          moneyFactor,
          RATE_PRECISION,
        )} ${factorType}`;
      }

      return '';
    };

    // for every lead, create a new column
    for (const lead of incentivesData.leads) {
      headers.push({
        className: selectedClass(lead),
        content: (
          <label className="lead-program">
            <Input
              onChange={() => {
                setSelectedLeadId(lead.leadId);
                setSelectedProgramId(lead.programId);
              }}
              name="selectedLeadId"
              checked={
                selectedLeadId === lead.leadId &&
                selectedProgramId === lead.programId
              }
              type="radio"
            />
            {lead.name} <br /> {getAprOrMoneyFactor(lead)}
          </label>
        ),
      });
    }

    // push empty cell to account for the more details button column
    headers.push({ content: '' });
    return headers;
  };

  const renderRow = (rebate: Rebate): Item[] => {
    const row: Item[] = [
      {
        content: (
          <div className="incentive-editor-selector">
            {renderIncentivesSelector(rebate)}
          </div>
        ),
      },
      {
        content: rebate.isLeadingRebate ? (
          <>
            <span className="leading-rebate-name"> {rebate.name}</span>
            <Tooltip
              width={200}
              text="This rebate comes with a lead incentive and is applied automatically."
            >
              <Icon name="info circle" />
            </Tooltip>
          </>
        ) : (
          rebate.name
        ),
      },
    ];

    // push a combined cash amount for each lead for this rebate
    for (const lead of incentivesData.leads) {
      const foundValue = rebate.values.find(v => v.leadId === lead.leadId);
      // rebates are either dealerCash or otherCash, so we can just take the first one we find (otherCash or dealerCash will be 0 respectively if it doesn't apply)
      const content = foundValue
        ? fmtMoney(foundValue.otherCash || foundValue.dealerCash || 0)
        : '-';

      row.push({
        content,
        className: [
          selectedClass(lead),
          appliedClass(lead, rebate),
          incompatibleClass(lead, rebate),
          boldClass(rebate),
        ]
          .filter(val => !!val)
          .join(' '),
      });
    }

    if (rebate.details) {
      row.push({
        content: (
          <button
            className="btn"
            onClick={() =>
              ProgramDetailsModal({
                program: { dealerProgramText: rebate.details },
              })
            }
          >
            More Details
          </button>
        ),
      });
    } else {
      // ensure there is still a table cell even if there are no details (for spacing)
      row.push({ content: '' });
    }
    return row;
  };

  const rebateTotal = (leadId: number, cashType: CashType) => {
    const applicableRebate = Array.from(selectedRebates).filter(
      r => !incompatibleIncentives.get(leadId)?.has(r),
    );

    return incentivesData.rebates
      .filter(r => applicableRebate.includes(r.rebateId))
      .map(r => {
        const foundValue = r.values.find(v => v.leadId === leadId);
        return foundValue?.[cashType] || 0;
      })
      .reduce((acc, val) => acc + val, 0);
  };

  const renderTotalCashRow = (cashType: CashType): Item[] => {
    const contentLabel = {
      dealerCash: 'Dealer Cash',
      otherCash: 'Other Cash',
    };

    const row: Item[] = [
      { content: '' },
      { content: `Total ${contentLabel[cashType]}` },
    ];

    for (const lead of incentivesData.leads) {
      const total = rebateTotal(lead.leadId, cashType);
      row.push({
        content: fmtMoney(total),
        className: selectedClass(lead),
      });
    }

    // render last cell so spacing is correct
    row.push({ content: '' });
    return row;
  };

  const renderSpacingRow = (): Item[] => {
    // two empty cells to account for the checkbox and rebate name columns
    const row: Item[] = [{ content: '' }, { content: '' }];

    // add empty cells for each lead
    for (const lead of incentivesData.leads) {
      row.push({
        content: '',
        className: selectedClass(lead),
      });
    }

    // add empty cell for the more details button
    row.push({ content: '' });
    return row;
  };

  const isDisabled = () => isLoadingIncentives || disabled;

  let rows: Item[][] = [];
  let footers: Item[][] = [];

  if (!isLoadingIncentives && !displayNoIncentivesAvailable) {
    rows = incentivesData.rebates.map(renderRow);
    footers = [
      // render a full row of empty cells to create a break between the incentives and the totals
      // full row needed to ensure the spacing is correct in the table display
      renderSpacingRow(),
      renderTotalCashRow('dealerCash'),
      renderTotalCashRow('otherCash'),
    ];
  }

  // dynamically build the title based on the finance type and offer months
  const buildTitle = () => {
    if (!activeOffer.financeType) {
      return ``;
    }

    const baseTitle = `${capitalize(activeOffer.financeType)} incentives`;

    if (isCashOfferData(activeOffer)) {
      return baseTitle;
    }

    return `${baseTitle} - ${activeOffer.months} months`;
  };

  const options = incentivesData.customerTypes.map(customerType => ({
    value: customerType.id,
    label: customerType.name,
  }));

  return (
    <>
      <h2 className="modal-title">{buildTitle()}</h2>
      {incentivesData.customerTypes.length > 1 && (
        <div className="customer-type-field col-sm-3">
          <label htmlFor="customerTypeField">Customer Type</label>
          <Select
            type="select"
            name="customerType"
            id="customerTypeField"
            options={options}
            value={(() =>
              options.find(o => o.value === selectedCustomerType))()}
            onChange={(option: { value: number }) =>
              setSelectedCustomerType(option.value)
            }
            className="select"
            containerClassName="select-container"
          />
        </div>
      )}
      <IncentivesTable
        className="ms-incentives-table"
        emptyMessage="No incentives available"
        footers={footers}
        headers={getHeaders()}
        rows={rows}
        isLoading={isLoadingIncentives || isLoadingIncentivesCompatibility}
      />

      <div className="modal-footer">
        <button
          key="save"
          className="modal-button save cc-button-primary"
          onClick={onSaveClicked}
          disabled={isDisabled()}
        >
          Save
        </button>
      </div>
    </>
  );
}

export default function EditMarketscanIncentives(
  props: EditMarketscanIncentivesProps,
) {
  return (
    <Modal
      isOpen={!!props.activeOffer}
      onRequestClose={() => {
        if (props.sendAmplitudeEvent) {
          props.sendAmplitudeEvent(ModalType.INCENTIVES, Action.CANCEL);
        }
        props.handleClose();
      }}
      style={scrollableModalStyles}
      ariaHideApp={false}
    >
      <span
        className="close"
        onClick={() => {
          if (props.sendAmplitudeEvent) {
            props.sendAmplitudeEvent(ModalType.INCENTIVES, Action.CANCEL);
          }
          props.handleClose();
        }}
      >
        X
      </span>
      <EditMarketscanIncentivesForm {...props} />
    </Modal>
  );
}
